// Shared state between Header (the burger button) and SideNav (the
// sidebar/drawer itself) so the two stay in sync without prop-drilling
// through App and every page.
//
// Two pieces of state because the burger has a dual role: on admin
// desktop it toggles the persistent sidebar's collapsed state; everywhere
// else it opens the overlay drawer.
//
// Collapsed state is tracked **per mode** (App vs Admin) with separate
// localStorage keys so the operator's preference for the operational
// portal doesn't have to match their preference for /admin. Defaults
// favour collapsed-in-App / expanded-in-Admin: when an admin pops back
// out to App they don't want a wide nav rail eating workspace, but when
// they're in Admin the sub-nav is the whole point of being there.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode, createElement } from "react";
import { useLocation } from "react-router-dom";

const APP_COLLAPSED_KEY = "jli-sidenav-collapsed-app";
const ADMIN_COLLAPSED_KEY = "jli-sidenav-collapsed-admin";
const PERSISTENT_BREAKPOINT = "(min-width: 1024px)";

interface SideNavCtx {
    /** Persistent-mode collapse state for the CURRENT route (App vs Admin). */
    collapsed: boolean;
    setCollapsed: (next: boolean) => void;
    /** Overlay/drawer-only mode open state. */
    drawerOpen: boolean;
    setDrawerOpen: (next: boolean) => void;
    /** True when viewport is wide enough for the persistent layout. */
    isDesktop: boolean;
    /** True when a scan page is rendered — forces drawer mode even on admin desktop. */
    scanMode: boolean;
}

const Ctx = createContext<SideNavCtx>({
    collapsed: false,
    setCollapsed: () => {},
    drawerOpen: false,
    setDrawerOpen: () => {},
    isDesktop: false,
    scanMode: false,
});

export const useSideNavContext = () => useContext(Ctx);

// Read the saved flag for a given mode. When nothing is stored yet, fall
// back to the per-mode default so first-time admins see App collapsed
// and Admin expanded automatically.
const readCollapsed = (key: string, fallback: boolean): boolean => {
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === "1") return true;
        if (raw === "0") return false;
        return fallback;
    } catch {
        return fallback;
    }
};

export const SideNavProvider = ({ children }: { children: ReactNode }) => {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith("/admin");

    const [appCollapsed, setAppCollapsedState] = useState<boolean>(() => readCollapsed(APP_COLLAPSED_KEY, true));
    const [adminCollapsed, setAdminCollapsedState] = useState<boolean>(() => readCollapsed(ADMIN_COLLAPSED_KEY, false));
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [scanMode, setScanMode] = useState<boolean>(false);
    const [isDesktop, setIsDesktop] = useState<boolean>(() => {
        // Guard for environments (older jsdom, SSR) where matchMedia is
        // missing or returns undefined — default to non-desktop so the
        // drawer/overlay code path runs and tests stay deterministic.
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
        const mq = window.matchMedia(PERSISTENT_BREAKPOINT);
        return mq ? mq.matches : false;
    });

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mq = window.matchMedia(PERSISTENT_BREAKPOINT);
        if (!mq || typeof mq.addEventListener !== "function") return;
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    // Scan-mode detection: any page that renders a `.scan-panel` element
    // is treated as a scan page, which suppresses the persistent sidebar
    // (so warehouse staff get the full screen for scanning) and falls
    // back to drawer-only access via the burger. An open dispatch order
    // (`.dispatch-page` — the pack/confirm/flag views) is treated the same
    // way: on desktop the persistent nav would otherwise sit over the
    // order workspace, so it's hidden until the burger reopens it. We
    // watch the DOM rather than asking each component to register, so
    // adding new scan/order pages requires zero context plumbing.
    useEffect(() => {
        if (typeof document === "undefined" || typeof MutationObserver !== "function") return;
        const update = () => setScanMode(document.querySelector(".scan-panel, .dispatch-page") !== null);
        update();
        const observer = new MutationObserver(update);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    // Active mode's collapse state. Toggling routes between App and
    // Admin doesn't fire a re-render of the consumer because we read
    // through the same context value — but the value itself recomputes
    // when isAdminRoute flips, so consumers always see the right state.
    const collapsed = isAdminRoute ? adminCollapsed : appCollapsed;

    const setCollapsed = useCallback((next: boolean) => {
        if (isAdminRoute) {
            setAdminCollapsedState(next);
            try { window.localStorage.setItem(ADMIN_COLLAPSED_KEY, next ? "1" : "0"); } catch { /* ignore */ }
        } else {
            setAppCollapsedState(next);
            try { window.localStorage.setItem(APP_COLLAPSED_KEY, next ? "1" : "0"); } catch { /* ignore */ }
        }
    }, [isAdminRoute]);

    const value = useMemo<SideNavCtx>(() => ({
        collapsed, setCollapsed, drawerOpen, setDrawerOpen, isDesktop, scanMode,
    }), [collapsed, setCollapsed, drawerOpen, isDesktop, scanMode]);

    return createElement(Ctx.Provider, { value }, children);
};
